import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

export default function useLiveQuery(path, params = {}) {
  const serialized = JSON.stringify(params);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshRef = useRef(() => {});
  const refresh = useCallback(() => refreshRef.current(), []);

  useEffect(() => {
    let alive = true, busy = false, revision = null;
    const controller = new AbortController();
    setData(null);
    setLoading(true);
    async function load(force = false) {
      if (busy || !alive || (!force && document.visibilityState === "hidden")) return;
      busy = true;
      try {
        const stamp = await api.get("/operations/revision", { signal: controller.signal });
        if (force || revision !== stamp.data.revision) {
          const res = await api.get(path, { params: JSON.parse(serialized), signal: controller.signal });
          if (alive) setData(res.data);
        }
        revision = stamp.data.revision;
        if (alive) { setError(""); setUpdatedAt(new Date()); }
      } catch (err) {
        if (alive && err.code !== "ERR_CANCELED") setError(formatApiError(err));
      } finally {
        busy = false;
        if (alive) setLoading(false);
      }
    }
    refreshRef.current = () => load(true);
    const focus = () => load(true);
    load(true);
    const timer = setInterval(() => load(), 3000);
    window.addEventListener("focus", focus);
    window.addEventListener("online", focus);
    document.addEventListener("visibilitychange", focus);
    return () => {
      alive = false; controller.abort(); clearInterval(timer);
      window.removeEventListener("focus", focus);
      window.removeEventListener("online", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, [path, serialized]);
  return { data, setData, error, loading, updatedAt, refresh };
}
